import React, { useEffect } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { a11yProps, TabPanel } from "shared/components/TabPanel";
import { useTranslation } from "react-i18next";
import { useSetAtom } from "jotai";
import {
  shouldGenerateIPAPhonemeAtom,
  shouldGenerateSampaPhonemeAtom,
} from "atoms";
import PronunciationMenuXSampa from "./PronunciationMenuXSampa";
import PronunciationMenuIPA from "./PronunciationMenuIPA";

interface PronunciationMenuTabsProps {
  selectedTabIndex: number;
  handleChangeTab: (event: React.SyntheticEvent, newValue: number) => void;
  selectedWord: string;
  userPhonemeIPA: string;
  setUserPhonemeIPA: React.Dispatch<React.SetStateAction<string>>;
  userPhonemeXSampa: string;
  setUserPhonemeXSampa: React.Dispatch<React.SetStateAction<string>>;
}

export default function PronunciationMenuTabs({
  selectedTabIndex,
  handleChangeTab,
  selectedWord,
  userPhonemeIPA,
  setUserPhonemeIPA,
  userPhonemeXSampa,
  setUserPhonemeXSampa,
}: PronunciationMenuTabsProps): JSX.Element {
  const { t } = useTranslation();
  const tabData = [
    { index: 0, label: t("tts.toolbarMenu.pronunciationMenu.ipaHeader") },
    { index: 1, label: t("tts.toolbarMenu.pronunciationMenu.xSampaHeader") },
  ];
  const setShouldGenerateIPAPhoneme = useSetAtom(shouldGenerateIPAPhonemeAtom);
  const setShouldGenerateSampaPhoneme = useSetAtom(
    shouldGenerateSampaPhonemeAtom
  );

  useEffect(() => {
    // Only set these to true if there's no existing phoneme value
    setShouldGenerateIPAPhoneme(!userPhonemeIPA);
    setShouldGenerateSampaPhoneme(!userPhonemeXSampa);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ border: 0 }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedTabIndex}
          onChange={handleChangeTab}
          aria-label="pronunciation-tabs"
          variant="fullWidth"
        >
          {tabData.map(({ index, label }) => (
            <Tab
              key={index}
              style={{ textTransform: "none" }}
              label={label}
              {...a11yProps(index)}
              disableRipple
            />
          ))}
        </Tabs>
      </Box>
      {tabData.map(({ index }) => {
        return index === 0 ? (
          <TabPanel key={index} value={selectedTabIndex} index={index}>
            <PronunciationMenuIPA
              selectedWord={selectedWord}
              userPhoneme={userPhonemeIPA}
              setPhoneme={setUserPhonemeIPA}
            />
          </TabPanel>
        ) : (
          <TabPanel key={index} value={selectedTabIndex} index={index}>
            <PronunciationMenuXSampa
              selectedWord={selectedWord}
              userPhoneme={userPhonemeXSampa}
              setPhoneme={setUserPhonemeXSampa}
            />
          </TabPanel>
        );
      })}
    </Box>
  );
}
